import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Filter, Plus, BarChart3, GitBranch, X, Building2 } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import { fetchScenarios, createScenario, updateScenario, deleteScenario, cloneScenario, setBaselineScenario } from '@/store/slices/scenariosSlice';
import { fetchPlanningPeriods } from '@/store/slices/planningPeriodsSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';

// Components
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassModal,
  GlassSelect,
  GlassLoadingSpinner,
  SectionReveal,
} from '@/design-system';
import ScenarioForm from './components/ScenarioForm';
import ScenarioCompareEnhanced from './components/ScenarioCompareEnhanced';
import ScenarioSelector from './components/ScenarioSelector';
import ScenarioDashboard from './ScenarioDashboard';

// Types
import type { Column } from '@/types';

interface Scenario {
  id: string;
  name: string;
  description?: string;
  planningPeriodId: string;
  isBaseline: boolean;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
  planningPeriod?: {
    id: string;
    name: string;
  };
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  _count?: {
    allocations: number;
    featureAllocations: number;
  };
}

const ScenariosPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { scenarios, isLoading } = useSelector((state: RootState) => state.scenarios);
  const { periods: planningPeriods } = useSelector((state: RootState) => state.planningPeriods);
  const { programs } = useSelector((state: RootState) => state.programs);
  const user = useSelector((state: RootState) => state.auth.user);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [selectedPlanningPeriod, setSelectedPlanningPeriod] = useState<string>('');
  const [comparisonScenarios, setComparisonScenarios] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [dashboardScenario, setDashboardScenario] = useState<Scenario | null>(null);
  const [ownershipFilter, setOwnershipFilter] = useState<'all' | 'mine'>(
    user?.role === 'PROGRAM_LEAD' ? 'mine' : 'all'
  );

  const planningPeriodList = planningPeriods || [];

  useEffect(() => {
    dispatch(fetchPlanningPeriods());
    dispatch(fetchPrograms({}));
  }, [dispatch]);

  useEffect(() => {
    if (selectedPlanningPeriod) {
      const params: { planningPeriodId?: string; createdById?: string } = {
        planningPeriodId: selectedPlanningPeriod,
      };
      if (ownershipFilter === 'mine' && user?.id) {
        params.createdById = user.id;
      }
      dispatch(fetchScenarios(params));
    }
  }, [dispatch, selectedPlanningPeriod, ownershipFilter, user?.id]);

  // Auto-select program for PROGRAM_LEAD with single program
  useEffect(() => {
    if (
      user?.role === 'PROGRAM_LEAD' &&
      programs?.length === 1 &&
      !selectedProgram
    ) {
      setSelectedProgram(programs[0].id);
    }
  }, [programs, user?.role, selectedProgram]);

  // Set default planning period
  useEffect(() => {
    if (planningPeriodList.length > 0 && !selectedPlanningPeriod) {
      const activePeriod = planningPeriodList.find(p => p.isActive);
      setSelectedPlanningPeriod(activePeriod?.id || planningPeriodList[0].id);
    }
  }, [planningPeriodList, selectedPlanningPeriod]);

  const handleCreate = async (data: Partial<Scenario>) => {
    try {
      await dispatch(createScenario({
        ...data,
        planningPeriodId: selectedPlanningPeriod,
      })).unwrap();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create scenario:', error);
    }
  };

  const handleEdit = async (data: Partial<Scenario>) => {
    if (!selectedScenario) return;
    
    try {
      await dispatch(updateScenario({ id: selectedScenario.id, data })).unwrap();
      setIsEditModalOpen(false);
      setSelectedScenario(null);
    } catch (error) {
      console.error('Failed to update scenario:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this scenario? This action cannot be undone.')) {
      try {
        await dispatch(deleteScenario(id)).unwrap();
      } catch (error) {
        console.error('Failed to delete scenario:', error);
      }
    }
  };

  const handleClone = async (scenario: Scenario) => {
    try {
      await dispatch(cloneScenario({
        id: scenario.id,
        data: {
          name: `${scenario.name} (Copy)`,
          description: `Cloned from ${scenario.name}`,
        }
      })).unwrap();
    } catch (error) {
      console.error('Failed to clone scenario:', error);
    }
  };

  const handleSetBaseline = async (id: string) => {
    if (window.confirm('Are you sure you want to set this as the baseline scenario?')) {
      try {
        await dispatch(setBaselineScenario(id)).unwrap();
      } catch (error) {
        console.error('Failed to set baseline scenario:', error);
      }
    }
  };

  const handleCompareScenarios = (scenarioIds: string[]) => {
    setComparisonScenarios(scenarioIds);
    setIsComparisonModalOpen(true);
  };

  const handleEditClick = (scenario: Scenario) => {
    setSelectedScenario(scenario);
    setIsEditModalOpen(true);
  };

  const handleViewDashboard = (scenario: Scenario) => {
    setDashboardScenario(scenario);
  };

  const handleBackFromDashboard = () => {
    setDashboardScenario(null);
  };

  const handleCompareFromDashboard = (scenarioId: string) => {
    // Find another scenario to compare with (e.g., the baseline)
    const baseline = scenarios.find((s) => s.isBaseline && s.id !== scenarioId);
    if (baseline) {
      setComparisonScenarios([scenarioId, baseline.id]);
      setIsComparisonModalOpen(true);
    }
  };

  const canManageScenario = (scenario: Scenario) => {
    const adminRoles = ['ADMIN', 'PMO', 'EXECUTIVE'];
    return scenario.createdById === user?.id || adminRoles.includes(user?.role || '');
  };

  const getStatusBadge = (scenario: Scenario) => {
    if (scenario.isBaseline) {
      return <GlassBadge variant="info">Baseline</GlassBadge>;
    }

    const allocationCount = (scenario._count?.allocations || 0) + (scenario._count?.featureAllocations || 0);
    if (allocationCount === 0) {
      return <GlassBadge variant="default">Empty</GlassBadge>;
    }

    return <GlassBadge variant="success">Active</GlassBadge>;
  };

  const columns: Column<Scenario>[] = [
    {
      key: 'name',
      header: 'Scenario Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-foreground text-[13px]">{value}</div>
          {row.description && (
            <div className="text-xs text-muted-foreground mt-1">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'planningPeriod',
      header: 'Planning Period',
      render: (_, row) => (
        <span className="text-foreground">
          {row.planningPeriod?.name || 'Unknown'}
        </span>
      ),
    },
    {
      key: 'allocations',
      header: 'Allocations',
      render: (_, row) => {
        const totalAllocations = (row._count?.allocations || 0) + (row._count?.featureAllocations || 0);
        return (
          <div className="text-foreground">
            <div>{row._count?.allocations || 0} EPIC allocations</div>
            <div className="text-sm text-muted-foreground">
              {row._count?.featureAllocations || 0} Feature allocations
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, row) => getStatusBadge(row),
    },
    {
      key: 'createdBy',
      header: 'Created By',
      render: (_, row) => (
        <span className="text-sm text-muted-foreground">
          {row.createdBy
            ? `${row.createdBy.firstName} ${row.createdBy.lastName}`
            : 'Unknown'}
        </span>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Last Modified',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, row) => (
        <div className="flex space-x-2">
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={() => handleViewDashboard(row)}
            leftIcon={<BarChart3 className="w-3 h-3" />}
          >
            Dashboard
          </GlassButton>
          {canManageScenario(row) && (
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={() => handleEditClick(row)}
            >
              Edit
            </GlassButton>
          )}
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={() => handleClone(row)}
          >
            Clone
          </GlassButton>
          {!row.isBaseline && canManageScenario(row) && (
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={() => handleSetBaseline(row.id)}
            >
              Set Baseline
            </GlassButton>
          )}
          {!row.isBaseline && canManageScenario(row) && (
            <GlassButton
              variant="secondary"
              size="sm"
              onClick={() => handleDelete(row.id)}
            >
              Delete
            </GlassButton>
          )}
        </div>
      ),
    },
  ];

  const planningPeriodOptions = planningPeriodList.map(period => ({
    value: period.id,
    label: period.name,
  }));

  const scenarioOptions = (scenarios || []).map(scenario => ({
    value: scenario.id,
    label: scenario.name,
    isBaseline: scenario.isBaseline,
  }));

  // If viewing a scenario dashboard, show the dashboard component
  if (dashboardScenario) {
    return (
      <ScenarioDashboard
        scenario={dashboardScenario}
        programId={selectedProgram || undefined}
        programName={
          selectedProgram
            ? (programs || []).find(p => p.id === selectedProgram)?.name
            : undefined
        }
        onBack={handleBackFromDashboard}
        onCompare={handleCompareFromDashboard}
        allScenarios={scenarios || []}
      />
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Scenario Management"
          subtitle="Create and compare what-if planning scenarios"
          icon={<GitBranch className="w-6 h-6" />}
          iconGradient="cyan"
          className="mb-6"
          actions={
            <div className="flex items-center gap-2">
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setShowFilters(true)}
                leftIcon={<Filter className="w-4 h-4" />}
              >
                Filters
              </GlassButton>
              <ScenarioSelector
                scenarios={scenarioOptions}
                onCompare={handleCompareScenarios}
              />
              <GlassButton
                variant="primary"
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Create Scenario
              </GlassButton>
            </div>
          }
        />
      </SectionReveal>

      {/* Filters Drawer */}
      <div
        className={`fixed inset-0 z-40 transition ${showFilters ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!showFilters}
      >
        <div
          className={`absolute inset-0 bg-black/25 backdrop-blur-sm transition-opacity ${showFilters ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setShowFilters(false)}
        />
        <div
          className={`absolute right-0 top-0 h-full w-80 bg-surface-glass border-l border-border-subtle shadow-xl transition-transform ${
            showFilters ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            <div>
              <h4 className="text-sm font-semibold text-foreground">Filters</h4>
              <p className="text-xs text-muted-foreground/60">Refine scenarios</p>
            </div>
            <GlassButton variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
              <X className="w-4 h-4" />
            </GlassButton>
          </div>
          <div className="p-4 space-y-4">
            <GlassSelect
              label="Planning Period"
              value={selectedPlanningPeriod}
              onChange={(value) => setSelectedPlanningPeriod(value)}
              options={[
                { value: '', label: 'Select Planning Period' },
                ...planningPeriodOptions
              ]}
            />
            <GlassSelect
              label="Program"
              value={selectedProgram}
              onChange={(value) => setSelectedProgram(value)}
              options={[
                { value: '', label: 'All Programs' },
                ...(programs || []).map(p => ({
                  value: p.id,
                  label: p.name,
                })),
              ]}
            />
            <GlassButton
              variant="ghost"
              onClick={() => {
                setSelectedPlanningPeriod('');
                setSelectedProgram('');
                setShowFilters(false);
              }}
              leftIcon={<X className="w-4 h-4" />}
              fullWidth
            >
              Clear Filters
            </GlassButton>
          </div>
        </div>
      </div>

      {/* Ownership Filter Toggle (Program Leads only) */}
      {user?.role === 'PROGRAM_LEAD' && selectedPlanningPeriod && (
        <SectionReveal delay={0.05}>
          <div className="flex gap-1 p-1 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] w-fit mb-4">
            <button
              onClick={() => setOwnershipFilter('mine')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                ownershipFilter === 'mine'
                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-[var(--bg-secondary)]'
              }`}
            >
              My Scenarios
            </button>
            <button
              onClick={() => setOwnershipFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                ownershipFilter === 'all'
                  ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-[var(--bg-secondary)]'
              }`}
            >
              All Scenarios
            </button>
          </div>
        </SectionReveal>
      )}

      {/* Active Program Indicator */}
      {selectedProgram && (
        <SectionReveal delay={0.08}>
          <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 w-fit">
            <Building2 className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-400 font-medium">
              Program: {(programs || []).find(p => p.id === selectedProgram)?.name || 'Unknown'}
            </span>
            <span className="text-xs text-muted-foreground">
              — dashboards will be scoped to this program
            </span>
            <button
              onClick={() => setSelectedProgram('')}
              className="ml-1 p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </SectionReveal>
      )}

      {/* Scenarios Table */}
      {selectedPlanningPeriod && (
        <SectionReveal delay={0.1}>
          <GlassCard padding="none" className="mb-4">
            <DataTable
              data={scenarios}
              columns={columns}
              loading={isLoading}
              searchPlaceholder="Search scenarios..."
              emptyMessage="No scenarios found. Create your first scenario to get started."
            />
          </GlassCard>
        </SectionReveal>
      )}

      {/* Floating New Scenario button */}
      <button
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
        onClick={() => setIsCreateModalOpen(true)}
        aria-label="Create Scenario"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Scenario"
        subtitle="Define a new what-if planning scenario"
        icon={<Plus className="w-5 h-5" />}
        iconGradient="cyan"
        size="lg"
      >
        <ScenarioForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreateModalOpen(false)}
          planningPeriods={planningPeriodList}
          selectedPlanningPeriodId={selectedPlanningPeriod}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedScenario(null);
        }}
        title="Edit Scenario"
        subtitle={selectedScenario?.name}
        icon={<GitBranch className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        {selectedScenario && (
          <ScenarioForm
            initialData={selectedScenario}
            onSubmit={handleEdit}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedScenario(null);
            }}
            planningPeriods={planningPeriodList}
            selectedPlanningPeriodId={selectedPlanningPeriod}
          />
        )}
      </GlassModal>

      {/* Enhanced Comparison Modal */}
      {comparisonScenarios.length >= 2 && (
        <ScenarioCompareEnhanced
          scenarioId={comparisonScenarios[0]}
          otherScenarioId={comparisonScenarios[1]}
          isOpen={isComparisonModalOpen}
          onClose={() => {
            setIsComparisonModalOpen(false);
            setComparisonScenarios([]);
          }}
        />
      )}
    </div>
  );
};

export default ScenariosPage;
